import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import styles from './StatusDropdown.module.scss';
import { removeTooltipContent, setTooltipContent } from '../../../store/tooltip/tooltip_actions';
import { useAppDispatch } from '../../../app/hooks';
import { defaultOptionValue } from '../../../utils/constants/commonConstants';
import { useCustomFilteringLogic } from './useCustomFilteringLogic';
import CaretIcon from '../../../assets/Svg/CaretIcon';
import { useFixedDropdownInModal } from '../../../utils/customHooks';

export type StatusDropdownOption = {
    label: string;
    value: string;
    backgroundColor: string;
    labelColor: string;
    customCaret?:
        | string
        | {
              height: string;
              width: string;
              colour: string;
          };
    overrideDisableRow?: boolean;
};

type Props = {
    dropdownOptions: StatusDropdownOption[];
    selectedOption: StatusDropdownOption;
    selectOption: (option: StatusDropdownOption) => void;
    disabled?: boolean;
    source?: string;
    customTrigger?: React.ReactNode;
    defaultOption?: StatusDropdownOption;
    showClearButton?: boolean;
    restrictBackwardMovement?: boolean;
    field?: string;
    customSelectedOptionIndex?: number;
    isConfigTableModalDropDown?: boolean;
    row?: Record<string, unknown>;
};

const StatusDropdown: React.FC<Props> = ({
    field = '',
    dropdownOptions = [],
    selectedOption,
    selectOption,
    disabled = false,
    source,
    customTrigger,
    defaultOption = defaultOptionValue,
    showClearButton = true,
    restrictBackwardMovement = false,
    customSelectedOptionIndex,
    isConfigTableModalDropDown = false,
    row,
}) => {
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);

    const selectedOptionIndex =
        customSelectedOptionIndex !== undefined
            ? customSelectedOptionIndex
            : dropdownOptions.findIndex((option) => option?.value?.toLowerCase() === selectedOption?.value?.toLowerCase());
    const { filteredOptions } = useCustomFilteringLogic(
        searchTerm,
        field,
        dropdownOptions,
        restrictBackwardMovement,
        selectedOptionIndex,
        row,
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        setSearchTerm(''); // Reset search when opening
    };

    const handleClickOutside = (event) => {
        const target = event.target as Node;
        if (dropdownRef.current && !dropdownRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
            setIsOpen(false);
        }
    };

    const handleOnChange = (option: StatusDropdownOption) => {
        selectOption(option);
        setIsOpen(false);
    };

    const handleMouseEnterName = (e) => {
        if (selectedOption.label)
            dispatch(
                setTooltipContent({
                    content: selectedOption.label,
                    target: e.currentTarget,
                }),
            );
    };

    const handleMouseLeaveName = () => {
        removeTooltipContent(dispatch);
    };

    const handleClearSelection = () => {
        selectOption(defaultOption);
        setIsOpen(false);
    };

    // Clone customTrigger to pass disabled prop if it's a valid React element
    const enhancedCustomTrigger =
        React.isValidElement(customTrigger) && disabled
            ? React.cloneElement(customTrigger as React.ReactElement, {
                  disabled: true,
              })
            : customTrigger;

    const handleCustomTriggerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            toggleDropdown();
        }
    };
    const dropdownPosition = useFixedDropdownInModal({
        isEnabled: isConfigTableModalDropDown,
        isOpen,
        triggerRef,
        dropdownRef,
        onClose: () => {
            setIsOpen(false);
        },
    });

    return (
        <div className={styles.StatusDropdown}>
            <div ref={triggerRef}>
                {customTrigger ? (
                    <div onClick={handleCustomTriggerClick} style={disabled ? { cursor: 'not-allowed', pointerEvents: 'none' } : undefined}>
                        {enhancedCustomTrigger}
                    </div>
                ) : (
                    <button
                        className={styles.DropdownToggle}
                        style={{
                            backgroundColor: selectedOption.backgroundColor,
                            color: selectedOption.labelColor,
                            cursor: source !== 'investor-deal' ? 'pointer' : 'default',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown();
                        }}
                    >
                        <span className={styles.SelectedText} onMouseEnter={handleMouseEnterName} onMouseLeave={handleMouseLeaveName}>
                            {selectedOption.label}
                        </span>
                        {!disabled &&
                            (typeof selectedOption.customCaret === 'string' ? (
                                <img
                                    className={clsx(styles.DropdownCaret, {
                                        [styles.CaretOpen]: isOpen,
                                    })}
                                    src={selectedOption.customCaret}
                                    alt="Caret"
                                />
                            ) : (
                                <span
                                    className={clsx(styles.DropdownCustomCaretIcon, {
                                        [styles.CaretOpenIcon]: isOpen,
                                    })}
                                >
                                    <CaretIcon
                                        height={selectedOption.customCaret?.height || '14'}
                                        width={selectedOption.customCaret?.width || '14'}
                                        colour={selectedOption.customCaret?.colour}
                                    />
                                </span>
                            ))}
                    </button>
                )}
            </div>
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className={clsx(styles.DropdownMenu, {
                        [styles.isConfigTableModalDropDown]: isConfigTableModalDropDown,
                    })}
                    style={
                        isConfigTableModalDropDown
                            ? {
                                  position: 'fixed',
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: '185px',
                              }
                            : undefined
                    }
                >
                    <input
                        type="text"
                        className={styles.SearchInputFilter}
                        placeholder="Search"
                        value={searchTerm}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {showClearButton && (
                        <button
                            className={styles.ClearButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearSelection();
                            }}
                        >
                            Clear Selection
                        </button>
                    )}
                    {filteredOptions.length > 0 &&
                        filteredOptions.map((option, index) => (
                            <button
                                key={`status-dropdown-${option.value}`}
                                className={clsx(styles.DropdownCell)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOnChange(option);
                                }}
                                title={option.label}
                            >
                                <div
                                    key={option.label}
                                    className={styles.OptionPill}
                                    style={{
                                        backgroundColor: option.backgroundColor,
                                        color: option.labelColor,
                                    }}
                                >
                                    {option.label}
                                </div>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

export default StatusDropdown;
